import { Component, OnInit, computed, effect, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import { forkJoin, switchMap } from 'rxjs';
import { reportInvalidFormFields } from '@app/utilities';
import { InternalTransferProxyService } from '../../../proxies/internal-transfer.proxy.service';
import { BankAccountProxyService, BankAccountSearchCommand } from '../../../proxies/bank-account.proxy.service';
import { BankAccount, BankAccountStatus } from '../../../models/bank-account/bank-account';
import { CashRequest } from '../../../models/cash-request/cash-request';
import {
  CASH_REQUEST_ENTITY_CODE,
  InternalTransfer,
  InternalTransferReceiveCommand,
  InternalTransferStatus,
  InternalTransferUpdate,
} from '../../../models/internal-transfer/internal-transfer';
import {
  InternalTransferFormGroup,
  TransferSettlementFormGroup,
  TransferSetupFormGroup,
} from '../../../models/internal-transfer/internal-transfer-form';
import { BankFeeFormGroup } from '../../../models/bank-transaction/bank-transaction-form';
import { BankMovementControls, PreviewLine } from '../../../models/bank-transaction/bank-movement-form';
import { BankMovementOption } from '../../shared/bank-movement-form/bank-movement-form.component';
import { BankFee, BankFeeType, BankInstrumentType, BankTransaction } from '../../../models/bank-transaction/bank-transaction';
import { BankTransactionProxyService } from '../../../proxies/bank-transaction.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { Currency, currencyLabel as fallbackCurrencyLabel } from '../../../utilities/currency.util';
import { RATE_DECIMALS } from '../../../utilities/settlement-rate.util';

interface EnumOption {
  label: string;
  value: number;
}

type TransferTab = 'outgoing' | 'incoming';

/**
 * Create, send, receive or return an internal transfer (#5089).
 *
 * <p><b>Two legs, not one movement.</b> The send books money out of the source bank and into Cash in
 * Transit; the receive books it out of Cash in Transit and into the destination. Between the two it belongs
 * to neither account, so each leg gets its own tab, its own bank reference and its own charges — they
 * reconcile against different banks' statements and each bank charges separately.</p>
 *
 * <p><b>Each leg is saved as the bank movement it is</b>, through the ordinary bank transaction endpoint.
 * The transfer document keeps only what is its own — the date, the request it answers, the notes — and even
 * the amount lives on the outgoing leg, because that movement is what takes the money out of the bank.</p>
 *
 * <p><b>The incoming gross is locked to the outgoing one.</b> What the receiving side supplies is the
 * charges; what landed is the remainder. That is why there is no difference gauge here any more — the two
 * sides cannot disagree, so there is nothing left to warn about.</p>
 */
@Component({
  selector: 'app-internal-transfer-details',
  templateUrl: './internal-transfer-details.component.html',
  styleUrl: './internal-transfer-details.component.scss',
})
export class InternalTransferDetailsComponent implements OnInit {
  /** Rate precision for the rate inputs — the API's (CurrencyConverter.RateDecimals, #5359). */
  protected readonly rateDecimals = RATE_DECIMALS;

  readonly InternalTransferStatus = InternalTransferStatus;

  /** A signal, not a plain field — computeds read it, and a computed over a mutable field caches its
   *  first value forever. */
  readonly id = signal<string | null>(null);
  readonly isLoading = signal(0);
  readonly saving = signal(false);

  readonly status = signal<number>(InternalTransferStatus.DRAFT);
  readonly transfer = signal<InternalTransfer | null>(null);

  /** Tab strip. A FormControl rather than a bare signal so it drives a p-selectbutton, which is how every
   *  other tabbed screen here is built — PrimeNG's own tab components are unused in this app. */
  readonly tabControl = new FormControl<TransferTab>('outgoing', { nonNullable: true });
  readonly activeTab = toSignal(this.tabControl.valueChanges, { initialValue: 'outgoing' as TransferTab });

  readonly fromAccount = signal<BankAccount | null>(null);
  readonly toAccount = signal<BankAccount | null>(null);

  /** The company's other active accounts, for step 1. */
  readonly destinationOptions = signal<BankAccount[]>([]);
  readonly cashRequestOptions = signal<CashRequest[]>([]);

  readonly returnDialogVisible = signal(false);

  private currencyLabels: Record<number, string> = {};
  private statusLabels: Record<number, string> = {};

  // ---- Where in the flow we are ────────────────────────────────────────────────

  /** Step 1: the source account is known (we came from it) but nothing has been created yet. */
  readonly isSetup = computed(() => this.id() === null);
  readonly isDraft = computed(() => this.status() === InternalTransferStatus.DRAFT);
  readonly isInTransit = computed(() => this.status() === InternalTransferStatus.IN_TRANSIT);
  readonly isClosed = computed(
    () =>
      this.status() === InternalTransferStatus.RECEIVED ||
      this.status() === InternalTransferStatus.RETURNED ||
      this.status() === InternalTransferStatus.CANCELLED,
  );

  /** Locked because it is genuinely unknown, not because it is derived — see the class comment. */
  readonly incomingTabEnabled = computed(() => !this.isDraft() && !this.isSetup());

  readonly tabOptions = computed(() => [
    { label: 'Outgoing leg', value: 'outgoing' as TransferTab, disabled: false },
    { label: 'Incoming leg', value: 'incoming' as TransferTab, disabled: !this.incomingTabEnabled() },
  ]);

  /**
   * Whether the bank converts on the way. Read off the ACCOUNTS rather than the stored
   * `destinationCurrency`, so step 1 — where nothing has been created yet — answers it the same way the
   * rest of the page does.
   */
  readonly converts = computed(() => {
    const from = this.fromAccount();
    const to = this.toAccount();
    return !!from && !!to && from.currency !== to.currency;
  });

  /** The currency the money lands in. A return comes home to the source, so it never converts. */
  readonly arrivalCurrency = computed(() => this.arrivalAccount()?.currency ?? this.fromAccount()?.currency ?? null);

  readonly arrivalCurrencyLabel = computed(() => {
    const currency = this.arrivalCurrency();
    return currency == null ? '' : (this.currencyLabels[currency] ?? fallbackCurrencyLabel(currency));
  });

  /** True when THIS leg converts — a receipt into a different currency. A return does not. */
  readonly incomingConverts = computed(() => {
    const arrival = this.arrivalCurrency();
    return arrival != null && arrival !== this.fromAccount()?.currency;
  });

  /**
   * Which side of the pair the rate is quoted in. Every convertible pair routes through USD, so exactly one
   * side is the pivot and the other is the one a person names — "Lempiras to the dollar".
   */
  readonly quotedCurrencyLabel = computed(() => {
    const from = this.fromAccount()?.currency;
    const arrival = this.arrivalCurrency();
    if (from == null || arrival == null) return '';
    const quoted = from === Currency.USD ? arrival : from;
    return this.currencyLabels[quoted] ?? fallbackCurrencyLabel(quoted);
  });

  readonly currencyLabel = computed(() => {
    const account = this.fromAccount();
    return account ? (this.currencyLabels[account.currency] ?? '') : '';
  });

  readonly statusLabel = computed(() => this.statusLabels[this.status()] ?? '');

  readonly statusSeverity = computed<'success' | 'warn' | 'danger' | 'info' | 'secondary'>(() => {
    switch (this.status()) {
      case InternalTransferStatus.RECEIVED:
        return 'success';
      case InternalTransferStatus.IN_TRANSIT:
        return 'warn';
      case InternalTransferStatus.RETURNED:
        return 'danger';
      case InternalTransferStatus.CANCELLED:
        return 'secondary';
      default:
        return 'info';
    }
  });

  /** Both entries once the transfer closes — the summary component takes a list. */
  readonly journalEntryIds = computed(() => {
    const t = this.transfer();
    return [t?.outgoingLeg?.journalEntryReference?.reference, t?.incomingLeg?.journalEntryReference?.reference];
  });

  readonly showAccounting = computed(() => this.journalEntryIds().some((id) => !!id));

  /**
   * Which step booked which entry. A transfer posts two, and `FI-2026-000050` beside `FI-2026-000052` does
   * not tell a reader which one sent the money — the numbers are consecutive and the accounts differ only
   * in which bank appears. The second one is captioned by what actually happened, so a returned transfer
   * does not claim to have been received.
   */
  readonly journalEntryLabels = computed<Record<string, string>>(() => {
    const transfer = this.transfer();
    const labels: Record<string, string> = {};

    const outgoing = transfer?.outgoingLeg?.journalEntryReference?.reference;
    if (outgoing) labels[outgoing] = this.text('InternalTransfer_Entry_Send', 'Send');

    const incoming = transfer?.incomingLeg?.journalEntryReference?.reference;
    if (incoming) {
      labels[incoming] = this.incomingIsReturn()
        ? this.text('InternalTransfer_Entry_Return', 'Return')
        : this.text('InternalTransfer_Entry_Receive', 'Receive');
    }

    return labels;
  });

  /** The `translateWF` pipe's rule, for the places a caption is built in TypeScript rather than a template. */
  private text(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated === key ? fallback : translated;
  }

  // ---- Forms ───────────────────────────────────────────────────────────────────

  /** Step 1 — which account receives the funds. */
  readonly setupForm = new FormGroup<TransferSetupFormGroup>({
    toAccountCode: new FormControl<string | null>(null, [Validators.required]),
  });

  readonly form = new FormGroup<InternalTransferFormGroup>({
    transferDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    cashRequestReference: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    instrumentType: new FormControl<number>(BankInstrumentType.WIRE, { nonNullable: true }),
    endToEndId: new FormControl<string | null>(null, [Validators.required]),
    uetr: new FormControl<string | null>(null),
    chargeBearer: new FormControl<string | null>(null),
    traceNumber: new FormControl<string | null>(null),
    sendBankingReference: new FormControl<string | null>(null),
    sendFees: new FormArray<FormGroup<BankFeeFormGroup>>([]),
  });

  readonly settlementForm = new FormGroup<TransferSettlementFormGroup>({
    // Disabled from birth. The gross is whatever was sent; the API refuses to change it, and offering an
    // input the server will ignore is worse than not offering one.
    grossAmount: new FormControl<number | null>({ value: null, disabled: true }),
    receivedOn: new FormControl<Date | null>(new Date(), [Validators.required]),
    bankingReference: new FormControl<string | null>(null),
    fees: new FormArray<FormGroup<BankFeeFormGroup>>([]),
    // Only meaningful when the bank converted; validators are attached in that case alone, so an ordinary
    // receipt is never asked for a rate it does not have.
    exchangeRateToUsd: new FormControl<number | null>(null),
    reason: new FormControl<string | null>(null),
  });

  /**
   * The outgoing leg, as the shared movement form sees it. No counter account — an internal transfer always
   * posts against Cash in Transit, which the API supplies rather than the user picking — and no memo, since
   * the transfer's notes are its own field on the document rather than the leg.
   */
  readonly sendControls: BankMovementControls = {
    amount: this.form.controls.amount,
    date: this.form.controls.transferDate,
    statementReference: this.form.controls.sendBankingReference,
    instrument: {
      type: this.form.controls.instrumentType,
      endToEndId: this.form.controls.endToEndId,
      uetr: this.form.controls.uetr,
      chargeBearer: this.form.controls.chargeBearer,
      traceNumber: this.form.controls.traceNumber,
    },
    fees: this.form.controls.sendFees,
  };

  /**
   * The incoming leg. No instrument at all: this records what a bank did TO us, so there is nothing of ours
   * to identify. Its `amount` is the gross the transfer sent — read-only — and the shared form's net row is
   * what landed once the charges come off.
   */
  readonly settlementControls: BankMovementControls = {
    amount: this.settlementForm.controls.grossAmount,
    date: this.settlementForm.controls.receivedOn,
    statementReference: this.settlementForm.controls.bankingReference,
    fees: this.settlementForm.controls.fees,
  };

  fieldLabels: Record<keyof InternalTransferFormGroup, string> = {
    transferDate: 'InternalTransfer_Field_TransferDate',
    amount: 'InternalTransfer_Field_Amount',
    cashRequestReference: 'InternalTransfer_Field_CashRequest',
    notes: 'InternalTransfer_Field_Notes',
    instrumentType: 'InternalTransfer_Field_InstrumentType',
    endToEndId: 'InternalTransfer_Field_EndToEndId',
    uetr: 'InternalTransfer_Field_Uetr',
    chargeBearer: 'InternalTransfer_Field_ChargeBearer',
    traceNumber: 'InternalTransfer_Field_TraceNumber',
    sendBankingReference: 'InternalTransfer_Field_SendBankingReference',
    sendFees: 'InternalTransfer_Field_SendFees',
  };

  settlementFieldLabels: Record<keyof TransferSettlementFormGroup, string> = {
    grossAmount: 'InternalTransfer_Field_AmountSent',
    receivedOn: 'InternalTransfer_Field_ReceivedOn',
    bankingReference: 'InternalTransfer_Field_ReceiveBankingReference',
    fees: 'InternalTransfer_Field_ReceiveFees',
    exchangeRateToUsd: 'InternalTransfer_Field_ExchangeRate',
    reason: 'InternalTransfer_Field_ReturnReason',
  };

  // ---- Options ─────────────────────────────────────────────────────────────────

  /**
   * A transfer is something we initiate, so BANK_INITIATED is not offered — it means the bank moved the
   * money by itself. DEPOSIT is not offered either — moving money between our own accounts is a
   * transfer, not a deposit. UNSPECIFIED stays available for a movement recorded after the fact with no
   * reference to hand.
   */
  readonly instrumentTypeOptions = signal<BankMovementOption[]>([
    { label: 'Wire', labelKey: 'BankInstrumentType_WIRE', value: BankInstrumentType.WIRE },
    { label: 'ACH', labelKey: 'BankInstrumentType_ACH', value: BankInstrumentType.ACH },
    { label: 'Transfer', labelKey: 'BankInstrumentType_TRANSFER', value: BankInstrumentType.TRANSFER },
    { label: 'Unspecified', labelKey: 'BankInstrumentType_UNSPECIFIED', value: BankInstrumentType.UNSPECIFIED },
  ]);

  readonly feeTypeOptions = signal<EnumOption[]>([
    { label: 'Bank fee', value: BankFeeType.BANK },
    { label: 'Security fee', value: BankFeeType.SECURITY },
  ]);

  // ---- Live amounts ────────────────────────────────────────────────────────────

  private readonly amountSig = signal<number | null>(null);
  private readonly sendFeesSig = signal<BankFee[]>([]);
  private readonly settlementFeesSig = signal<BankFee[]>([]);

  /** What left the source account: off the outgoing leg once it exists, off the form while it is being typed. */
  readonly sentAmount = computed(() => this.transfer()?.amount || this.amountSig() || 0);
  readonly totalSendFees = computed(() => this.sendFeesSig().reduce((sum, f) => sum + (f.amount || 0), 0));
  readonly totalSettlementFees = computed(() => this.settlementFeesSig().reduce((sum, f) => sum + (f.amount || 0), 0));

  /** Live value of the rate input, so the arrival figure and the preview move as it is typed. */
  private readonly rateSig = toSignal(this.settlementForm.controls.exchangeRateToUsd.valueChanges, {
    initialValue: this.settlementForm.controls.exchangeRateToUsd.value,
  });

  /**
   * What arrives at the far end, in the destination's own money.
   *
   * <p>Derived from the rate rather than typed, and derived the same way the API derives it — the quote
   * names the non-USD side, so it multiplies when that side is the arrival and divides when it is the
   * source. A second, independently-typed figure here would be a number the server ignores.</p>
   *
   * <p>Falls back to what was sent whenever nothing converts, which is every ordinary transfer and every
   * return.</p>
   */
  readonly amountArrived = computed(() => {
    if (!this.incomingConverts()) return this.sentAmount();

    const rate = this.rateSig() ?? 0;
    if (rate <= 0) return 0;

    const sent = this.sentAmount();
    return this.round2(this.arrivalCurrency() === Currency.USD ? sent / rate : sent * rate);
  });

  /** Derived, never typed — the gross is locked, so what landed is whatever the charges left behind. On a
   *  converted receipt the gross is what ARRIVED, and the charges are the receiving bank's, in its money. */
  readonly amountLanded = computed(() => this.amountArrived() - this.totalSettlementFees());

  private round2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  // ---- The cash request being answered (#5135) ─────────────────────────────────
  //
  // A transfer that answers a cash request is funding a week, and the person at the bank portal needs the
  // figure the week asked for. It used to be reachable only by opening the request in another tab.

  /** Live value of the picker, so everything below reacts to a change without an explicit handler. */
  private readonly cashRequestRef = toSignal(this.form.controls.cashRequestReference.valueChanges, {
    initialValue: this.form.controls.cashRequestReference.value,
  });

  readonly selectedCashRequest = computed<CashRequest | null>(() => {
    const reference = this.cashRequestRef();
    return reference ? (this.cashRequestOptions().find((r) => r.id === reference) ?? null) : null;
  });

  /** "12,345.67 USD" — grouped, always two decimals, so a figure read off the screen and retyped into a
   *  bank portal cannot lose a digit to a missing separator. Prefers the page's enum labels and falls back
   *  to the shared map, which matters before the enums have loaded. */
  /** A currency's display code, for rows the page has not resolved into a signal — a picker option. */
  currencyCode(currency: number | null | undefined): string {
    return currency == null ? '' : (this.currencyLabels[currency] ?? fallbackCurrencyLabel(currency));
  }

  money(amount: number | null | undefined, currency: number | null | undefined): string {
    const value = (amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const label = currency == null ? '' : (this.currencyLabels[currency] ?? fallbackCurrencyLabel(currency));
    return label ? `${value} ${label}` : value;
  }

  /** What the selected week asks for. A cash request is planned in USD, so this is dollars by construction. */
  readonly cashRequestTotalLabel = computed(() => {
    const request = this.selectedCashRequest();
    return request ? this.money(request.totalAmount, request.currency) : '';
  });

  /**
   * Whether this transfer moves the same money the request is stated in.
   *
   * <p>An internal transfer moves the SOURCE ACCOUNT's currency, which is often HNL, while a cash request
   * is always USD. Subtracting one from the other would produce a confident number that means nothing, so
   * the shortfall is only offered when the two agree and the mismatch is named outright when they do
   * not.</p>
   */
  readonly cashRequestComparable = computed(() => {
    const request = this.selectedCashRequest();
    const account = this.fromAccount();
    return !!request && !!account && request.currency === account.currency;
  });

  /** Known to differ — NOT merely "not known to match". Before the source account resolves the two are
   *  simply unknown, and warning about a currency mismatch then would be inventing one. */
  readonly cashRequestCurrencyMismatch = computed(() => {
    const request = this.selectedCashRequest();
    const account = this.fromAccount();
    return !!request && !!account && request.currency !== account.currency;
  });

  /** Sent minus asked-for, in the shared currency. Positive = more than the week needs. */
  readonly cashRequestGap = computed(() => {
    if (!this.cashRequestComparable()) return 0;
    return Math.round((this.sentAmount() - (this.selectedCashRequest()?.totalAmount ?? 0)) * 100) / 100;
  });

  readonly cashRequestShortfall = computed(() => this.cashRequestComparable() && Math.abs(this.cashRequestGap()) >= 0.01);
  readonly isUnderCashRequest = computed(() => this.cashRequestGap() < 0);
  readonly cashRequestGapLabel = computed(() => this.money(Math.abs(this.cashRequestGap()), this.fromAccount()?.currency));

  /** Options for the picker: the number, the week it covers, and what it asks for — so the figure is visible
   *  while CHOOSING, not only after. Filtering runs over the whole label, so a week or an amount finds it. */
  readonly cashRequestSelectOptions = computed(() =>
    this.cashRequestOptions().map((request) => ({
      label: [request.number, this.weekLabel(request), this.money(request.totalAmount, request.currency)].filter((p) => !!p).join(' · '),
      value: request.id ?? '',
    })),
  );

  private weekLabel(request: CashRequest): string {
    if (!request.weekStart || !request.weekEnd) return '';
    const format = (value: string) => {
      const date = new Date(value);
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    };
    return `${format(request.weekStart)} – ${format(request.weekEnd)}`;
  }

  /** The incoming movement, once someone has started recording it. Absent for the whole of the wire's flight. */
  readonly incomingLeg = computed(() => this.transfer()?.incomingLeg ?? null);
  readonly hasIncomingLeg = computed(() => !!this.incomingLeg());

  /** A leg aimed back at the source account is a return being recorded, not a receipt. */
  readonly incomingIsReturn = computed(() => {
    const leg = this.incomingLeg();
    return !!leg && leg.to?.code === this.transfer()?.fromAccountCode;
  });

  /** Where the money lands. A return comes home to the source account instead of going on to the destination. */
  readonly arrivalAccount = computed(() => (this.incomingIsReturn() ? this.fromAccount() : this.toAccount()));

  // ---- Journal entry previews ──────────────────────────────────────────────────

  /**
   * The send entry as it will be posted. Built here rather than fetched so it updates as the user types.
   *
   * The Cash in Transit and fee accounts are config-driven on the API, so the UI cannot know their numbers;
   * they are named by role and resolved server-side.
   */
  readonly sendPreviewLines = computed<PreviewLine[]>(() => {
    const source = this.fromAccount();
    const gross = this.amountSig() ?? 0;
    if (!source || gross <= 0) return [];

    return [
      { accountNumber: null, accountName: 'Cash in transit', debit: gross, credit: 0 },
      ...this.feeLines(this.sendFeesSig()),
      {
        accountNumber: source.glAccountNumber ?? null,
        accountName: `${source.name} (${source.bankName})`,
        debit: 0,
        credit: gross + this.totalSendFees(),
      },
    ];
  });

  /**
   * The receive (or return) entry. Charges come out of what arrives, so the bank line is what landed.
   *
   * <p>When the bank converts, the two sides are in different money: cash in transit is credited what LEFT,
   * in the currency it left in — which is what makes it clear to exactly zero — and the bank and its charges
   * are debited what ARRIVED. Each line names its own currency, because otherwise the reader is invited to
   * subtract two figures that are not comparable.</p>
   */
  readonly settlementPreviewLines = computed<PreviewLine[]>(() => {
    const arrival = this.arrivalAccount();
    const sent = this.sentAmount();
    if (!arrival || sent <= 0) return [];

    const converts = this.incomingConverts();
    const arrived = this.amountArrived();

    // Nothing to preview until a rate is entered: every arrival-side figure would read zero.
    if (converts && arrived <= 0) return [];

    const arrivalCurrency = converts ? this.arrivalCurrencyLabel() : undefined;

    return [
      {
        accountNumber: arrival.glAccountNumber ?? null,
        accountName: `${arrival.name} (${arrival.bankName})`,
        debit: this.amountLanded(),
        credit: 0,
        currency: arrivalCurrency,
      },
      ...this.feeLines(this.settlementFeesSig(), arrivalCurrency),
      {
        accountNumber: null,
        accountName: 'Cash in transit',
        debit: 0,
        credit: sent,
        currency: converts ? this.currencyLabel() : undefined,
      },
    ];
  });

  private feeLines(fees: BankFee[], currency?: string): PreviewLine[] {
    return fees
      .filter((f) => (f.amount || 0) > 0)
      .map((f) => ({
        accountNumber: null,
        accountName: this.feeTypeOptions().find((o) => o.value === f.type)?.label ?? '',
        debit: f.amount,
        credit: 0,
        currency,
      }));
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: InternalTransferProxyService,
    private bankTransactionService: BankTransactionProxyService,
    private bankAccountService: BankAccountProxyService,
    private enumService: EnumService,
    private translate: TranslateService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.statusLabels = this.enumService.getEnum('InternalTransferStatus').values as Record<number, string>;

    // The shared movement form owns the fee table and the instrument rules; these subscriptions only mirror
    // the rows into signals so the entry previews and the difference gauge recompute as they are edited.
    this.form.controls.amount.valueChanges.subscribe((v) => this.amountSig.set(v));
    this.form.controls.sendFees.valueChanges.subscribe(() => this.sendFeesSig.set(this.readFees(this.sendFeeRows)));
    this.settlementForm.controls.fees.valueChanges.subscribe(() => this.settlementFeesSig.set(this.readFees(this.settlementFeeRows)));

    // The gross on a converted receipt is what ARRIVED, which follows from the rate. Mirrored onto the
    // control because the shared movement form reads it from there — and one-way, from the derived figure
    // to the disabled control, so there is still exactly one place the number is decided.
    effect(() => {
      const arrived = this.amountArrived();
      if (!this.incomingConverts()) return;
      this.settlementForm.controls.grossAmount.setValue(arrived || null, { emitEvent: false });
    });

    // The rate is required exactly when the bank converts, and the accounts that decide that arrive
    // asynchronously — so the validator follows the answer rather than being set once at load, when it
    // would still be reading nulls.
    effect(() => {
      const control = this.settlementForm.controls.exchangeRateToUsd;
      if (this.incomingConverts()) {
        control.setValidators([Validators.required, Validators.min(0.000001)]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id')?.trim();
      const fromCode = params.get('from')?.trim();

      if (id) {
        this.id.set(id);
        this.loadById();
        this.loadCashRequests();
        return;
      }

      // No id: step 1 of creation, started from the source account's page.
      if (!fromCode) {
        this.router.navigate(['/internal-transfers']);
        return;
      }
      this.id.set(null);
      this.breadcrumbService.setCurrent('Common_New');
      this.loadAccountsForSetup(fromCode);
      this.loadCashRequests();
    });
  }

  // ---- Step 1: choose the receiving account ────────────────────────────────────

  /**
   * Step 1's two questions: which account we are sending FROM, and which accounts it can send TO.
   *
   * <p>The destinations are asked of the API rather than filtered here. Which currencies a transfer may
   * convert between is a rule the server enforces, and a copy of it in a component is the arrangement that
   * went unenforced for as long as nobody called the endpoint directly — the same mistake `PaymentCurrencyGate`
   * was written to undo. So the picker asks, and offers exactly what would be accepted.</p>
   */
  private loadAccountsForSetup(fromCode: string): void {
    this.isLoading.update((n) => n + 1);
    const command: BankAccountSearchCommand = { statuses: [BankAccountStatus.ACTIVE] };

    forkJoin({
      accounts: this.bankAccountService.search(command, 1, 200, true),
      destinations: this.service.getTransferableAccounts(fromCode),
    }).subscribe({
      next: ({ accounts, destinations }) => {
        this.fromAccount.set(accounts.data?.items?.find((a) => a.code === fromCode) ?? null);
        this.destinationOptions.set(destinations.data ?? []);
        this.isLoading.update((n) => n - 1);
      },
      error: () => {
        this.destinationOptions.set([]);
        this.isLoading.update((n) => n - 1);
      },
    });
  }

  onDestinationChange(code: string | null): void {
    this.toAccount.set(this.destinationOptions().find((a) => a.code === code) ?? null);
  }

  /** Creates the draft and moves to step 2. Cancelling before this point creates nothing at all. */
  continueFromSetup(): void {
    if (this.setupForm.invalid || !this.fromAccount()) {
      this.setupForm.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.service
      .createDraft({
        fromAccountCode: this.fromAccount()!.code!,
        toAccountCode: this.setupForm.controls.toAccountCode.value!,
        transferDate: new Date().toISOString(),
      })
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          const created = response.data;
          if (created?.id) {
            this.id.set(created.id);
            this.hydrate(created);
            this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
          }
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error creating internal transfer:', error);
        },
      });
  }

  // ---- Load ────────────────────────────────────────────────────────────────────

  private loadById(): void {
    this.isLoading.update((n) => n + 1);
    this.service.getById(this.id()!).subscribe({
      next: (response) => {
        if (response.data) this.hydrate(response.data);
        this.isLoading.update((n) => n - 1);
      },
      error: (error) => {
        this.isLoading.update((n) => n - 1);
        console.error('Error fetching internal transfer:', error);
      },
    });
  }

  /**
   * Everything the server returned, into every control. Right after a load or an action that closed the
   * form — wrong after a partial save, which is what {@link applyDocument} and {@link applyStatusOnly}
   * are for.
   */
  private hydrate(data: InternalTransfer): void {
    this.applyDocument(data);
    this.applyMovements(data);
    this.applyEditability();
    if (this.isInTransit()) this.tabControl.setValue('incoming');
  }

  /**
   * The transfer's own fields. Safe to call after saving the transfer, because these are exactly what that
   * save just wrote — anything the user has typed into a movement and not yet saved is left alone.
   */
  private applyDocument(data: InternalTransfer): void {
    this.transfer.set(data);
    this.status.set(data.status);
    this.breadcrumbService.setCurrent(data.number);

    this.form.patchValue({
      transferDate: data.transferDate ? new Date(data.transferDate) : null,
      cashRequestReference: data.cashRequest?.reference ?? null,
      notes: data.notes,
    });

    this.resolveAccounts(data.fromAccountCode, data.toAccountCode);
  }

  /** The two legs, into the movement forms. Overwrites unsaved edits, so only on a full hydrate. */
  private applyMovements(data: InternalTransfer): void {
    this.form.patchValue({
      amount: data.amount || null,
      sendBankingReference: data.outgoingLeg?.bankingReference ?? null,
      instrumentType: data.outgoingLeg?.instrument?.type ?? BankInstrumentType.WIRE,
      endToEndId: data.outgoingLeg?.instrument?.endToEndId ?? null,
      uetr: data.outgoingLeg?.instrument?.uetr ?? null,
      chargeBearer: data.outgoingLeg?.instrument?.chargeBearer ?? null,
      traceNumber: data.outgoingLeg?.instrument?.traceNumber ?? null,
    });

    this.setFeeRows(this.sendFeeRows, data.outgoingLeg?.fees ?? []);
    this.setFeeRows(this.settlementFeeRows, data.incomingLeg?.fees ?? []);

    // The gross is the transfer's, not the receiving side's — patched through even when no incoming leg
    // exists yet, so the receipt form opens already showing what it has to account for.
    // On a converted transfer the receipt has to account for what ARRIVED, not what was sent — and until a
    // rate is entered that figure is not known, so the effect in the constructor fills it in from there.
    this.settlementForm.patchValue({
      grossAmount: data.amountArrived || data.amount || null,
      receivedOn: data.incomingLeg?.transactionDate ? new Date(data.incomingLeg.transactionDate) : new Date(),
      bankingReference: data.incomingLeg?.bankingReference ?? null,
      exchangeRateToUsd: data.exchangeRateToUsd ?? null,
      reason: data.returnReason ?? null,
    });
  }

  /**
   * Refreshes what the page reads off the document — the movement numbers, the derived amount, the status —
   * without touching a single control. For after saving a movement: the form already holds what was saved,
   * and re-patching it would only be a chance to lose an edit elsewhere on the page.
   */
  private applyStatusOnly(data: InternalTransfer): void {
    this.transfer.set(data);
    this.status.set(data.status);
    this.applyEditability();
  }

  private applyEditability(): void {
    if (!this.isDraft()) this.form.disable({ emitEvent: false });
    if (this.isClosed()) this.settlementForm.disable({ emitEvent: false });
  }

  private resolveAccounts(fromCode?: string | null, toCode?: string | null): void {
    const command: BankAccountSearchCommand = { statuses: [BankAccountStatus.ACTIVE] };
    this.bankAccountService.search(command, 1, 200, true).subscribe({
      next: (response) => {
        const accounts = response.data?.items ?? [];
        this.fromAccount.set(accounts.find((a) => a.code === fromCode) ?? null);
        this.toAccount.set(accounts.find((a) => a.code === toCode) ?? null);
      },
      error: () => {
        this.fromAccount.set(null);
        this.toAccount.set(null);
      },
    });
  }

  /** Reloaded once the transfer id is known so its own linked request stays selectable (see the proxy). */
  private loadCashRequests(): void {
    this.service.getLinkableCashRequests(this.id()).subscribe({
      next: (response) => this.cashRequestOptions.set(response.data ?? []),
      error: () => this.cashRequestOptions.set([]),
    });
  }

  // ---- Fees ────────────────────────────────────────────────────────────────────
  //
  // Adding and removing rows belongs to the shared movement form, which owns the table. What stays here is
  // rebuilding the rows from what the server returned, and reading them back for the previews.

  get sendFeeRows(): FormArray<FormGroup<BankFeeFormGroup>> {
    return this.form.controls.sendFees;
  }

  get settlementFeeRows(): FormArray<FormGroup<BankFeeFormGroup>> {
    return this.settlementForm.controls.fees;
  }

  private static newFeeRow(type = BankFeeType.BANK, amount: number | null = null): FormGroup<BankFeeFormGroup> {
    return new FormGroup<BankFeeFormGroup>({
      type: new FormControl<number>(type, { nonNullable: true }),
      amount: new FormControl<number | null>(amount, [Validators.min(0.01)]),
    });
  }

  private setFeeRows(rows: FormArray<FormGroup<BankFeeFormGroup>>, fees: BankFee[]): void {
    rows.clear({ emitEvent: false });
    for (const fee of fees) rows.push(InternalTransferDetailsComponent.newFeeRow(fee.type, fee.amount), { emitEvent: false });
    rows.updateValueAndValidity();
  }

  private readFees(rows: FormArray<FormGroup<BankFeeFormGroup>>): BankFee[] {
    return rows.controls.map((g) => ({
      type: g.controls.type.value as BankFeeType,
      amount: g.controls.amount.value ?? 0,
    }));
  }

  // ---- Endpoint identity ───────────────────────────────────────────────────────

  /**
   * How an endpoint is named on screen: the bank, then where it is. A code answers "which record is this"
   * and nothing else — `BNK-006 → BNK-005` tells a treasurer moving money nothing about whether they picked
   * the right two banks, which is the one thing they need to be sure of before sending.
   */
  endpointTitle(account: BankAccount | null): string {
    if (!account) return '—';
    const country = (account.country ?? '').trim();
    return country ? `${account.bankName} — ${country}` : account.bankName;
  }

  /**
   * The detail line: code, last four of the account number, currency.
   *
   * <p>The code stays because it is what the API keys on and what every other screen shows. The masked
   * number is what separates two accounts at the same bank — Atlántida holds one in dollars and one in
   * lempiras, and the bank name alone cannot tell them apart.</p>
   */
  endpointDetail(account: BankAccount | null): string {
    if (!account) return '';
    const digits = (account.bankAccountNumber ?? '').replace(/\D/g, '');
    const masked = digits.length > 4 ? `····${digits.slice(-4)}` : digits;
    return [account.code, masked, this.currencyLabels[account.currency]].filter((part) => !!part).join(' · ');
  }

  /**
   * The movement number for one side of the transfer, once a leg exists. This is what a treasurer matches
   * against the bank statement, so it belongs beside the account rather than only on the leg's own screen.
   */
  legNumber(side: 'outgoing' | 'incoming'): string | null {
    const key = side === 'outgoing' ? this.transfer()?.outgoingKey : this.transfer()?.incomingKey;
    return key?.number || null;
  }

  // ---- Actions ─────────────────────────────────────────────────────────────────

  /**
   * Saves the transfer itself: when, the request it answers, and its notes.
   *
   * <p>Not the amount — that belongs to the outgoing movement and is saved with it by {@link saveLeg}. The
   * two are different documents and either can be saved without the other.</p>
   */
  saveDraft(): void {
    if (!this.id()) return;
    if (this.form.controls.transferDate.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.service.updateDraft(this.id()!, this.buildTransferUpdate()).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        // Document fields only. A full hydrate here would overwrite an amount or a charge the user has
        // typed into the movement beside this and not yet saved.
        if (response.data) this.applyDocument(response.data);
      },
      error: (error) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        console.error('Error saving transfer draft:', error);
      },
    });
  }

  private buildTransferUpdate(): InternalTransferUpdate {
    const v = this.form.getRawValue();
    const cashRequest = this.cashRequestOptions().find((r) => r.id === v.cashRequestReference);
    return {
      transferDate: (v.transferDate ?? new Date()).toISOString(),
      cashRequest: cashRequest
        ? { reference: cashRequest.id!, number: cashRequest.number ?? '', entityDefinitionCode: CASH_REQUEST_ENTITY_CODE }
        : null,
      notes: v.notes,
    };
  }

  /**
   * Saves the outgoing leg — the amount, how the money is going out, what the source bank charged, and the
   * reference its statement will show.
   *
   * <p>Through the ordinary bank transaction endpoint, because that is what this is: a draft movement out
   * of the source account. The transfer created it and stamped its own key on it, and the API restores the
   * accounts and direction from the stored leg, so nothing here can retarget which banks are involved.</p>
   *
   * <p>Separate from {@link saveDraft} on purpose. The transfer and its movement are different documents;
   * folding them into one button would mean a single save that half-succeeds.</p>
   */
  saveLeg(): void {
    const leg = this.buildOutgoingLeg();
    if (!leg) return;

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.bankTransactionService
      .updateDraft(leg.id!, leg)
      // Re-read the transfer for the movement number and the amount it now derives — but apply none of it
      // to the form, which already holds what was just saved and may hold unsaved Context edits too.
      .pipe(switchMap(() => this.service.getById(this.id()!)))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          if (response.data) this.applyStatusOnly(response.data);
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error saving the outgoing movement:', error);
        },
      });
  }

  /**
   * The outgoing leg as a whole movement, built from what the server last returned plus what the form
   * holds now. Null when there is nothing to save or the instrument rules are unmet.
   */
  private buildOutgoingLeg(): BankTransaction | null {
    const existing = this.transfer()?.outgoingLeg;
    if (!this.id()) return null;

    // Every draft has an outgoing movement — the API creates one on read if an older transfer lacks it. If
    // one is still missing, the save has nothing to write to, and returning quietly would look exactly like
    // a button that does not work.
    if (!existing?.id) {
      console.error('Internal transfer has no outgoing movement to save.');
      return null;
    }

    if (this.form.controls.endToEndId.invalid || this.form.controls.traceNumber.invalid || this.form.controls.amount.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return null;
    }

    const v = this.form.getRawValue();
    const amount = v.amount ?? 0;

    return {
      ...existing,
      transactionDate: (v.transferDate ?? new Date()).toISOString(),
      // Both endpoints carry the gross; the API mirrors them anyway, but sending a consistent pair keeps
      // the request readable in a network log.
      from: { ...existing.from!, amount },
      to: { ...existing.to!, amount },
      instrument: {
        type: v.instrumentType,
        // Empty strings become null: the API treats '' as absent, but null is what the field means.
        endToEndId: v.endToEndId || null,
        uetr: v.uetr || null,
        chargeBearer: v.chargeBearer || null,
        traceNumber: v.traceNumber || null,
        effectiveDate: null,
      },
      fees: this.sendFeesSig().filter((f) => f.amount > 0),
      bankingReference: v.sendBankingReference || null,
    };
  }

  /** Saves both documents, then sends what was saved — otherwise the API books the last saved state while
   *  the user is looking at their unsaved edits. */
  send(): void {
    if (!this.id()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }
    const leg = this.buildOutgoingLeg();
    if (!leg) return;

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);

    // Transfer first, then the movement, then send. The leg's date follows the document's, so saving the
    // transfer second would leave the movement carrying a date the document no longer holds.
    this.service
      .updateDraft(this.id()!, this.buildTransferUpdate())
      .pipe(
        switchMap(() => this.bankTransactionService.updateDraft(leg.id!, leg)),
        switchMap(() => this.service.send(this.id()!)),
      )
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          if (response.data) this.hydrate(response.data);
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error sending transfer:', error);
        },
      });
  }

  // ---- The incoming leg ────────────────────────────────────────────────────────

  /**
   * Starts recording what arrived. Until this is pressed no incoming movement exists at all — nothing true
   * is known about a receipt before someone is looking at a statement, and a draft conjured at send time
   * would sit in the destination bank's list claiming a date nobody has seen.
   */
  startIncomingLeg(isReturn: boolean): void {
    if (!this.id()) return;

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.service.createIncomingLeg(this.id()!, isReturn).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        if (response.data) this.hydrate(response.data);
      },
      error: (error) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        console.error('Error starting the incoming movement:', error);
      },
    });
  }

  /** Throws the unposted movement away — the way back out of having started the wrong one. */
  discardIncomingLeg(): void {
    if (!this.id()) return;

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.service.discardIncomingLeg(this.id()!).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        this.setFeeRows(this.settlementFeeRows, []);
        if (response.data) this.hydrate(response.data);
      },
      error: (error) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        console.error('Error discarding the incoming movement:', error);
      },
    });
  }

  /** Saves the incoming movement's charges, date and reference. The gross is not among them. */
  saveIncomingLeg(): void {
    const leg = this.buildIncomingLeg();
    if (!leg) return;

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.bankTransactionService
      .updateDraft(leg.id!, leg)
      .pipe(switchMap(() => this.service.getById(this.id()!)))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          if (response.data) this.applyStatusOnly(response.data);
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error saving the incoming movement:', error);
        },
      });
  }

  private buildIncomingLeg(): BankTransaction | null {
    const existing = this.incomingLeg();
    if (!this.id() || !existing?.id) return null;

    if (this.settlementForm.controls.receivedOn.invalid) {
      this.settlementForm.markAllAsTouched();
      reportInvalidFormFields(this.settlementForm, this.settlementFieldLabels, this.translate);
      return null;
    }

    const v = this.settlementForm.getRawValue();
    return {
      ...existing,
      transactionDate: (v.receivedOn ?? new Date()).toISOString(),
      fees: this.settlementFeesSig().filter((f) => f.amount > 0),
      bankingReference: v.bankingReference || null,
    };
  }

  /** Saves the movement, then posts it. Same reason as {@link send}: post what was saved, not what was typed. */
  receive(): void {
    // The rate is not saved with the movement — it belongs to the transfer, not to the bank line — so it is
    // checked here, before anything is written, rather than being discovered by the API after the leg saved.
    if (this.incomingConverts() && !((this.rateSig() ?? 0) > 0)) {
      this.settlementForm.controls.exchangeRateToUsd.markAsTouched();
      reportInvalidFormFields(this.settlementForm, this.settlementFieldLabels, this.translate);
      return;
    }

    const leg = this.buildIncomingLeg();
    if (!leg) return;

    const command: InternalTransferReceiveCommand = this.incomingConverts() ? { exchangeRateToUsd: this.rateSig() } : {};

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.bankTransactionService
      .updateDraft(leg.id!, leg)
      .pipe(switchMap(() => this.service.receive(this.id()!, command)))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          if (response.data) this.hydrate(response.data);
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error confirming receipt:', error);
        },
      });
  }

  openReturnDialog(): void {
    this.settlementForm.controls.reason.setValidators([Validators.required]);
    this.settlementForm.controls.reason.updateValueAndValidity({ emitEvent: false });
    this.returnDialogVisible.set(true);
  }

  closeReturnDialog(): void {
    this.settlementForm.controls.reason.clearValidators();
    this.settlementForm.controls.reason.updateValueAndValidity({ emitEvent: false });
    this.returnDialogVisible.set(false);
  }

  confirmReturn(): void {
    const leg = this.buildIncomingLeg();
    const reason = this.settlementForm.controls.reason.value?.trim();
    if (!leg || !reason) {
      this.settlementForm.controls.reason.markAsTouched();
      return;
    }

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    this.bankTransactionService
      .updateDraft(leg.id!, leg)
      .pipe(switchMap(() => this.service.return(this.id()!, { reason })))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          this.returnDialogVisible.set(false);
          if (response.data) this.hydrate(response.data);
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error returning transfer:', error);
        },
      });
  }

  cancelTransfer(): void {
    if (!this.id()) {
      this.router.navigate(['/internal-transfers']);
      return;
    }

    this.isLoading.update((n) => n + 1);
    this.service.cancel(this.id()!).subscribe({
      next: () => {
        this.isLoading.update((n) => n - 1);
        this.router.navigate(['/internal-transfers']);
      },
      error: (error) => {
        this.isLoading.update((n) => n - 1);
        console.error('Error cancelling transfer:', error);
      },
    });
  }

  /** Step 1 has created nothing, so backing out is a plain navigation. */
  abandonSetup(): void {
    this.router.navigate(['/bank-accounts']);
  }

  onAttachment(event: Event, input: HTMLInputElement): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    input.value = '';
    if (!file || !this.id()) return;

    this.isLoading.update((n) => n + 1);
    this.service.uploadAttachment(this.id()!, file).subscribe({
      next: (response) => {
        this.isLoading.update((n) => n - 1);
        if (response.data) this.hydrate(response.data);
      },
      error: (error) => {
        this.isLoading.update((n) => n - 1);
        console.error('Error uploading attachment:', error);
      },
    });
  }

  openAttachment(url: string): void {
    window.open(url, '_blank');
  }

  fileName(name: string): string {
    return name.split('/').pop() || name;
  }

  back(): void {
    this.router.navigate(['/internal-transfers']);
  }
}
